import { Box, Collapse, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import type { GridApiPremium, GridExceljsProcessInput } from '@mui/x-data-grid-premium';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import { now, vyDate } from '@vy/utils';
import type { MutableRefObject } from 'react';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import type { FilterLineType } from '../../../../../components/new-design/vyTableFilter';
import { useRecurringRevenueReport } from '../../../../../hooks/requests/use-recurring-revenue-report';
import { useFiltersParam } from '../../../../../hooks/search-params/use-filters-param';
import { useGridColumnSettings } from '../../../../../hooks/use-grid-column-settings';
import { useSearchWithSearchParams } from '../../../../../hooks/use-search-with-search-params';
import { formatDate } from '../../../../../utils/date';
import { userMessage } from '../../../../../utils/vy-toasts';
import { LocalStorageKeys } from '../../../../../constants/local-storage-keys';
import { getLocalStorageString, setLocalStorageString } from '../../../../../utils/local-storage';
import { ReportTable } from '../../../reports-components/ReportTable';
import { REPORT_TYPES } from '../../../reports-page/reports-page-types/report-type';
import { useRecurringRevenueAggregations } from '../../hooks/use-recurring-revenue-aggregations';
import { useRecurringRevenueFilterFields } from '../../hooks/use-recurring-revenue-filter-fields';
import type { ChartPeriodMode } from '../../utils/chart-movements';
import { buildRecurringRevenueExport } from '../../utils/build-recurring-revenue-export';
import type { Workbook } from 'exceljs';
import { RecurringRevenueReportChart } from '../RecurringRevenueReportChart/RecurringRevenueReportChart';
import { RecurringRevenueReportTable } from '../RecurringRevenueReportTable';
import {
  BASE_COLUMN_FIELDS,
  useRecurringRevenueReportColumns,
  type MonthlyValueMode,
} from '../RecurringRevenueReportTable/RecurringRevenueReportTableColumns';
import { RecurringRevenueReportToolbar } from '../RecurringRevenueReportToolbar';

const FILTERS_PARAM_NAME = 'recurringRevenueFilters';
const EMPTY_FILTERS: FilterLineType[] = [];

type RecurringRevenueReportContentProps = {
  reportId?: string;
};

export const RecurringRevenueReportContent: React.FC<RecurringRevenueReportContentProps> = ({ reportId }) => {
  const { data: recurringRevenueReportData, isLoading } = useRecurringRevenueReport(reportId);
  const [searchText, setSearchText] = useSearchWithSearchParams();
  // Defer the search text so typing doesn't block on the row-filter pass.
  const deferredSearchText = useDeferredValue(searchText);
  const { report, creationDate } = recurringRevenueReportData;
  const apiRef = useGridApiRef();

  const filterFields = useRecurringRevenueFilterFields(report);
  const [rawFilters, setFilters] = useFiltersParam<FilterLineType>(FILTERS_PARAM_NAME, filterFields);
  const filters = rawFilters ?? EMPTY_FILTERS;

  const defaultFromDate = useMemo(() => vyDate().subtract(11, 'month').startOf('month').toDate(), []);
  const defaultToDate = useMemo(() => vyDate().endOf('month').toDate(), []);

  const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
  const [toDate, setToDate] = useState<Date | null>(defaultToDate);
  const [showChart, setShowChart] = useState(false);
  const [chartPeriodMode, setChartPeriodMode] = useState<ChartPeriodMode>('monthly');
  const [viewMode, setViewMode] = useState<MonthlyValueMode>(
    () => (getLocalStorageString(LocalStorageKeys.ArrMrrReportViewMode) === 'mrr' ? 'mrr' : 'arr'),
  );

  useEffect(() => {
    setLocalStorageString(LocalStorageKeys.ArrMrrReportViewMode, viewMode);
  }, [viewMode]);

  const effectiveFromDate = fromDate ?? defaultFromDate;
  const effectiveToDate = toDate ?? defaultToDate;

  // Heavy filter + tree expansion + per-month metrics run in a Web Worker.
  const { bundle, error: aggregationError, isAggregating } = useRecurringRevenueAggregations({ report, filters });

  // Surface worker failures so the page doesn't silently hang at "no bundle".
  useEffect(() => {
    if (aggregationError) {
      console.error('[recurring-revenue] aggregation worker failed', aggregationError);
      userMessage('error', `Failed to compute recurring revenue report: ${aggregationError}`);
    }
  }, [aggregationError]);

  const columns = useRecurringRevenueReportColumns(report, effectiveFromDate, effectiveToDate, viewMode);

  const dynamicColumnFields = useMemo(() => {
    const dynamicColumns = columns.filter((column) => !BASE_COLUMN_FIELDS.has(column.field));
    return dynamicColumns.map((column) => column.field);
  }, [columns]);

  const { openSettings, closeSettings, columnSettings, handleColumnVisibilityChange } = useGridColumnSettings(
    apiRef as MutableRefObject<GridApiCommunity>,
    columns,
    dynamicColumnFields,
  );

  const handleExport = () => {
    if (!bundle) {
      userMessage('error', 'Report is still loading — please try again in a moment');
      return;
    }

    if (!apiRef.current?.exportDataAsExcel) {
      console.error('[recurring-revenue] exportDataAsExcel not available on apiRef');
      userMessage('error', 'Excel export unavailable');
      return;
    }

    const fileName = `ARR / MRR Report ${formatDate(now())}`;

    try {
      const result = apiRef.current.exportDataAsExcel({
        fileName,
        exceljsPostProcess: async (processInput: GridExceljsProcessInput) => {
          buildRecurringRevenueExport(processInput.workbook as unknown as Workbook, {
            filteredReport: bundle.filteredReport,
            fromDate: effectiveFromDate,
            toDate: effectiveToDate,
            viewMode,
          });
        },
      });

      Promise.resolve(result).then(
        () => userMessage('success', 'Download complete'),
        (error: unknown) => {
          console.error('[recurring-revenue] exportDataAsExcel failed', error);
          userMessage('error', 'Failed to download Excel');
        },
      );
    } catch (error) {
      console.error('[recurring-revenue] exportDataAsExcel threw', error);
      userMessage('error', 'Failed to download Excel');
    }
  };

  return (
    <>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          fontSize: "20px",
          mb: 1
        }}>
        ARR / MRR Report
      </Typography>
      <ReportTable
        title="ARR / MRR Report"
        hideTitle
        exportCsvFileName="ARR_MRR Report"
        reportCreationDate={creationDate}
        reportType={REPORT_TYPES.RECURRING_REVENUE}
        apiRef={apiRef as MutableRefObject<GridApiCommunity>}
        hideMenu
      >
        <RecurringRevenueReportToolbar
          onSearchChange={(value) => setSearchText(value)}
          searchValue={searchText ?? ''}
          fromDate={effectiveFromDate}
          onFromDateChange={setFromDate}
          toDate={effectiveToDate}
          onToDateChange={setToDate}
          filterFields={filterFields}
          filters={filters}
          onFiltersChange={setFilters}
          onColumnSettingsOpen={openSettings}
          onColumnSettingsClose={closeSettings}
          columnSettings={columnSettings}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onExport={handleExport}
          showChart={showChart}
          onShowChartChange={setShowChart}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Collapse in={showChart} timeout={350} unmountOnExit sx={{ flexShrink: 0 }}>
            <RecurringRevenueReportChart
              filteredReport={bundle?.filteredReport ?? []}
              fromDate={effectiveFromDate}
              toDate={effectiveToDate}
              periodMode={chartPeriodMode}
              onPeriodModeChange={setChartPeriodMode}
              searchText={searchText ?? undefined}
            />
          </Collapse>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {(bundle || isAggregating) && (
              <RecurringRevenueReportTable
                isLoading={isLoading || isAggregating}
                searchText={deferredSearchText ?? undefined}
                apiRef={apiRef as MutableRefObject<GridApiPremium>}
                filteredReport={bundle?.filteredReport ?? []}
                expandedRows={bundle?.expandedRows ?? []}
                columns={columns}
              />
            )}
          </Box>
        </Box>
      </ReportTable>
    </>
  );
};
